import React, { useState } from 'react'
import { useLocation } from '@reach/router'
import Helmet from './Helmet'
import { getLangs, getUrlForLang } from 'ptz-i18n'
import { getCurrentLangKey } from '../utils'
import { IntlProvider } from 'react-intl'
import '@formatjs/intl-pluralrules/polyfill'
import styled, { css, ThemeProvider } from 'styled-components'
import media from 'css-in-js-media'
import Cookies from 'universal-cookie'

import Footer from './Footer'
import Header from './Header'
import ReserveForm from './ReserveForm'
import SmoothScroll from './SmoothScroll'
import { useSiteMetadata } from '../hooks/MetaData'
import LayoutContext from './LayoutContext'
import GlobalStyle, { theme } from '../styles/GlobalStyle'
import CookiesNotice from './CookiesNotices'
import ZoomSlideshow from './ZoomSlideshow'

import { motion } from 'framer-motion'

import { TransitionState } from 'gatsby-plugin-transition-link'
import { useEffect } from 'react'
import { navigate } from 'gatsby'
import InteractiveMapPopup from './InteractiveMapPopup'
import HomeIntro from './HomeIntro'

const cookies = new Cookies()

const Wrapper = styled.div`
    ${(props) =>
        props.stopScroll &&
        css`
            ${media('<desktop')} {
                overflow: hidden;
                height: 100vh;
            }
        `}
`

const Main = styled.main``

const Loading = styled(motion.div)`
    background: black;
    z-index: 10000;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;
`

const getRedirectLanguage = () => {
    if (typeof navigator === `undefined`) {
        return 'en'
    }

    const lang = navigator && navigator.language && navigator.language.split('-')[0]
    if (!lang) return 'en'

    switch (lang) {
        case 'de':
            return 'de'
        default:
            return 'en'
    }
}

const Layout: React.FC<{
    children?: object
    noticeBar?: string
    headerBackground?: boolean
}> = ({ children, noticeBar, headerBackground }) => {
    const homeIntroLoadedBefore = cookies.get('home-intro-loaded') == 'yes'
    const [ isHomeIntroLoaded, setIsHomeIntroLoaded ] = useState(false);
    const location = useLocation()
    const isHomePage = location.pathname == '/' || location.pathname == '/de/'
    const url = location.pathname
    const LanguageLabels = {
        'en-GB': { langKeyShort: 'en', labelFull: 'English', labelDesktop: 'EN (English)' },
        de: { langKeyShort: 'de', labelFull: 'German', labelDesktop: 'DE (German)' },
    }

    const [SidebarOpen, setSidebarOpen] = useState(false)
    const [isLanguageMenuOpen, setLanguageMenuOpen] = useState(false)
    const [isGDPR, setGDPR] = useState(false)

    const [isSlideshow, setSlideshow] = useState([])
    const [isZoomOpen, setZoomOpen] = useState(false)
    const [ActiveZomSlide, setActiveZomSlide] = useState([])

    const [isMap, setMap] = useState([])
    const [isMapOpen, setMapOpen] = useState(false)
    const [ActiveMapSlide, setActiveMapSlide] = useState([])

    const { langs, defaultLangKey } = useSiteMetadata().languages
    const langKey = getCurrentLangKey(langs, defaultLangKey, url)
    const homeLink = `/${langKey}/`.replace(`/${defaultLangKey}/`, '/')
    const langsMenu = getLangs(langs, langKey, getUrlForLang(homeLink, url)).map((item) => ({
        ...item,
        link: item.link.replace(`/${defaultLangKey}/`, '/'),
        langKeyShort: LanguageLabels[item.langKey].langKeyShort,
        labelFull: LanguageLabels[item.langKey].labelFull,
        labelDesktop: LanguageLabels[item.langKey].labelDesktop,
        prefix: homeLink,
    }))

    const CurrentLanguage = langsMenu.find((i: { selected: boolean }) => i.selected == true)

    const i18nMessages = require(`../data/messages/${langKey}`)

    useEffect(() => {
        const urlLang = getRedirectLanguage()
        const LanguageDetectedBefore = localStorage.getItem('PozalaLanguageDetected')
        if (CurrentLanguage.langKeyShort == 'en' && urlLang != 'en' && !LanguageDetectedBefore) {
            navigate(`/${urlLang}${url}`, { replace: true })
            localStorage.setItem('PozalaLanguageDetected', 'yes')
        }
    }, [])

    useEffect(() => {
        setIsHomeIntroLoaded(homeIntroLoadedBefore)
    }, [homeIntroLoadedBefore])

    return (
        <IntlProvider locale={langKey} messages={i18nMessages}>
            <ThemeProvider theme={theme}>
                <LayoutContext.Provider
                    value={{
                        languageData: langsMenu,
                        currentLang: CurrentLanguage,
                        SidebarOpen,
                        setSidebarOpen,
                        isLanguageMenuOpen,
                        setLanguageMenuOpen,
                        isGDPR,
                        setGDPR,
                        isSlideshow,
                        setSlideshow,
                        isZoomOpen,
                        setZoomOpen,
                        ActiveZomSlide,
                        setActiveZomSlide,
                        isMap,
                        setMap,
                        isMapOpen,
                        setMapOpen,
                        ActiveMapSlide,
                        setActiveMapSlide,
                    }}
                >
                    <TransitionState>
                        {({ transitionStatus }) => (
                            <>
                                <GlobalStyle />
                                <Helmet
                                    title="Pozala"
                                    meta={[
                                        { name: 'description', content: '' },
                                        {
                                            name: 'keywords',
                                            content: '',
                                        },
                                    ]}
                                />
                                <ReserveForm />
                                <Loading
                                    initial={{ opacity: 1, display: 'block' }}
                                    animate={
                                        transitionStatus === 'exiting'
                                            ? { opacity: 1, display: 'block' }
                                            : { opacity: 0, transitionEnd: { display: 'none' } }
                                    }
                                    transition={{
                                        duration: 0.5,
                                    }}
                                ></Loading>
                                {isHomePage && !isHomeIntroLoaded && <HomeIntro />}
                                <Header headerBackground={headerBackground} />
                                <CookiesNotice />
                                <SmoothScroll>
                                    <Wrapper>
                                        <Main>{children}</Main>
                                        <Footer />
                                    </Wrapper>
                                </SmoothScroll>
                            </>
                        )}
                    </TransitionState>
                    <InteractiveMapPopup />
                    <ZoomSlideshow />
                </LayoutContext.Provider>
            </ThemeProvider>
        </IntlProvider>
    )
}

export default Layout
